import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import {
    ResponsiveCenteredGridItem,
    ToggleButton,
    ToggleContainer,
} from './styles';

import { Grid, Box } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import * as moment from 'moment';
import 'moment/locale/pt-br';
import DateRangePicker from '~/components/DateRangePicker';
import Header from '~/components/Header';
import NPSForm from '~/components/NPSForm';
import SalesTerms from '~/components/SalesTerms';
import api, { maisVendasApi } from '~/services/api';
import Snackbar from '~/util/SnackBar';
import {
    onlineChannels,
    physicalChannels,
} from '~/util/mapChannelsPhysicalAndOnline';
import { handleOrdersResponse } from '~/util/handleOrdersResponse';
import { shouldRemoveExternalProducts } from '~/util/functions';
import { ANIMALE, OFFPREMIUM } from '~/util/brands';

import {
    formatPrice,
    formatWithCent,
    formatCurrecyStringToNumberString,
    formatDecimalToPercentage,
} from '~/util/format';
import getDateRange from '~/util/getDateRange';
import { signOut } from '../../store/modules/auth/actions';
import DesktopLayout from './DesktopLayout';
import { changeShouldShowNPS } from '../../store/modules/user/actions';
import { useLocation } from 'react-router-dom';

const DATA_SOURCES = {
    LOJA: 'Loja',
    VENDEDOR: 'Vendedor',
};

function CotaCombinada() {
    const dispatch = useDispatch();
    const [aggregatedCapturedOrders, setAggregatedCapturedOrders] = useState(0);
    const [capturedOrdersIndicators, setCapturedOrdersIndicators] = useState(
        {}
    );
    const [totalOrdersIndicators, setTotalOrdersIndicators] = useState({});
    const [indicatorsText, setIndicatorsText] = useState({});
    const [quota, setQuota] = useState({});
    const [salesPercentage, setSalesPercentage] = useState('');
    const [salesValueAsNumber, setSalesValueAsNumber] = useState(0);
    const [orders, setOrders] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);
    const [balanceText, setBalanceText] = useState(0);
    const [panelLoaded, setPanelLoaded] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [showNPSForm, setShowNPSForm] = useState(false);
    const [shouldLogout, setShouldLogout] = useState(false);
    const [brandFilter, setBrandFilter] = useState('myBrands');
    const [dataSource, setDataSource] = useState(DATA_SOURCES.LOJA);
    const [aggregatedOrdersByPeriod, setAggregatedOrdersByPeriod] = useState(
        []
    );
    const [cachedData, setCachedData] = useState({
        [DATA_SOURCES.LOJA]: null,
        [DATA_SOURCES.VENDEDOR]: null,
    });
    const userProfile = useSelector(state => state.user.profile);
    const location = useLocation();
    const { initialDate, finalDate, isFaturado, period } = useSelector(
        state => state.status
    );
    // não remover, condição para mostrar os valores corretos antes da implementação da lógica de vendas externas da off premium.
    const beforeExternalOffPremiumDateLimit = new Date('2024-09-27');
    const dateRange = getDateRange(initialDate, finalDate, 'DD/MM/YYYY');
    const stateSetters = {
        quota: setQuota,
        aggregatedCapturedOrders: setAggregatedCapturedOrders,
        capturedOrdersIndicators: setCapturedOrdersIndicators,
        totalOrdersIndicators: setTotalOrdersIndicators,
        chartData: setChartData,
        balanceText: setBalanceText,
        aggregatedOrdersByPeriod: setAggregatedOrdersByPeriod,
        orders: setOrders,
        salesValueAsNumber: setSalesValueAsNumber,
    };

    const handleToggle = () => {
        setDataSource(currentSource =>
            currentSource === DATA_SOURCES.LOJA
                ? DATA_SOURCES.VENDEDOR
                : DATA_SOURCES.LOJA
        );
    };

    async function handleSubmit() {
        try {
            await api.post('/nps', {
                answer: '',
                comment: '',
                isSalesperson: userProfile.isSalesperson,
                login: userProfile.isSalesperson
                    ? userProfile.salesCode
                    : userProfile.login,
            });

            dispatch(changeShouldShowNPS(false));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
        }
    }

    useEffect(() => {
        setCachedData({
            [DATA_SOURCES.LOJA]: null,
            [DATA_SOURCES.VENDEDOR]: null,
        });
    }, [initialDate, finalDate]);

    useEffect(() => {
        if (shouldLogout) {
            dispatch(signOut());
        }
    }, [dispatch, shouldLogout]);

    const checkIfCanMakeRequests = useCallback(() => {
        return finalDate && userProfile?.users_sales_code[0].codigo_vendedor;
    }, [finalDate, userProfile]);

    useEffect(() => {
        const isFarm = userProfile?.marcaFilial === 2;
        const beforeDateLimit = new Date() < new Date('2023-05-26');
        const shouldAnswerNPS = userProfile?.shouldAnswerNPS;

        if (shouldAnswerNPS) {
            if (!(beforeDateLimit && isFarm)) {
                setShowNPSForm(userProfile.shouldAnswerNPS);
            }
        }
    }, [userProfile]);

    useEffect(() => {
        const newIndicatorsText = {
            totalValue: 0,
            totalAverageValue: 0,
            totalOrderQuantity: 0,
            totalProductQuantity: 0,
            totalAverageQuantity: 0,

            onlineTotalValue: 0,
            onlineAverageValue: 0,
            onlineAverageQuantity: 0,
            onlineOrderQuantity: 0,
            onlineProductQuantity: 0,

            physicalTotalValue: 0,
            physicalAverageValue: 0,
            physicalAverageQuantity: 0,
            physicalOrderQuantity: 0,
            physicalProductQuantity: 0,
        };
        const ordersIndicators = capturedOrdersIndicators;
        Object.entries(ordersIndicators).forEach(
            ([indicatorChannel, indicatorValue]) => {
                if (onlineChannels.includes(indicatorChannel)) {
                    newIndicatorsText.onlineTotalValue += indicatorValue.value;
                    newIndicatorsText.onlineOrderQuantity +=
                        indicatorValue.serviceQuantity;
                    newIndicatorsText.onlineProductQuantity +=
                        indicatorValue.productQuantity;
                }
                if (physicalChannels.includes(indicatorChannel)) {
                    newIndicatorsText.physicalTotalValue +=
                        indicatorValue.value;
                    newIndicatorsText.physicalOrderQuantity +=
                        indicatorValue.serviceQuantity;
                    newIndicatorsText.physicalProductQuantity +=
                        indicatorValue.productQuantity;
                }
            }
        );

        newIndicatorsText.onlineAverageQuantity = (
            newIndicatorsText.onlineProductQuantity /
                newIndicatorsText.onlineOrderQuantity || 0
        ).toFixed(2);

        newIndicatorsText.onlineAverageValue =
            newIndicatorsText.onlineTotalValue /
                newIndicatorsText.onlineOrderQuantity || 0;

        newIndicatorsText.physicalAverageQuantity = (
            newIndicatorsText.physicalProductQuantity /
                newIndicatorsText.physicalOrderQuantity || 0
        ).toFixed(2);

        newIndicatorsText.physicalAverageValue =
            newIndicatorsText.physicalTotalValue /
                newIndicatorsText.physicalOrderQuantity || 0;

        if (totalOrdersIndicators) {
            if (
                userProfile &&
                Object.keys(totalOrdersIndicators).length !== 0
            ) {
                const totalIndicators =
                    totalOrdersIndicators[userProfile.marcaFilial];

                newIndicatorsText.totalAverageValue =
                    totalIndicators.value / totalIndicators.serviceQuantity;
                newIndicatorsText.totalAverageQuantity = (
                    totalIndicators.productQuantity /
                    totalIndicators.serviceQuantity
                ).toFixed(2);
                newIndicatorsText.totalValue = totalIndicators.value;
                newIndicatorsText.totalOrderQuantity =
                    totalIndicators.serviceQuantity;
                newIndicatorsText.totalProductQuantity =
                    totalIndicators.productQuantity;
            }
        }

        const canFormat = [
            'onlineTotalValue',
            'physicalTotalValue',
            'totalValue',
            'totalAverageValue',
            'physicalAverageValue',
            'onlineAverageValue',
        ];

        Object.keys(newIndicatorsText).forEach(key => {
            if (canFormat.includes(key)) {
                newIndicatorsText[key] = formatWithCent(newIndicatorsText[key]);
            }
        });

        setIndicatorsText(newIndicatorsText);
    }, [capturedOrdersIndicators, totalOrdersIndicators, userProfile]);

    useEffect(() => {
        if (aggregatedCapturedOrders && aggregatedCapturedOrders[0] === '-') {
            return setSalesPercentage('0%');
        }
        const salesValue = formatCurrecyStringToNumberString(
            aggregatedCapturedOrders
        );
        const quotaValue = formatCurrecyStringToNumberString(quota.totalQuota);
        const newSalesPercentage = formatDecimalToPercentage(
            Number(salesValue) / Number(quotaValue)
        );
        setSalesPercentage(newSalesPercentage);
        setSalesValueAsNumber(Number(salesValue));
    }, [aggregatedCapturedOrders, quota.totalQuota]);

    const getBrandsParamsForRequests = useCallback(() => {
        const allBrandsIds = Array.from(
            { length: 16 },
            (_, brandId) => brandId + 1
        );
        let myBrands;

        if (dataSource === DATA_SOURCES.LOJA) {
            myBrands = userProfile.users_brands.map(
                brandObj => brandObj.id_marca
            );
        } else {
            myBrands = userProfile.marcaFilial;
        }

        switch (brandFilter) {
            case 'allBrands':
                return [];
            case 'myBrands':
                return myBrands;
            case 'otherBrands':
                const myBrandsArray = Array.isArray(myBrands)
                    ? myBrands
                    : [myBrands];
                return allBrandsIds.filter(
                    allBrandsId => !myBrandsArray.includes(allBrandsId)
                );
            default:
                return [];
        }
    }, [brandFilter, userProfile, dataSource]);

    useEffect(() => {
        const data = cachedData[dataSource];

        if (data) {
            Object.keys(data).forEach(key => {
                const setterFunction = stateSetters[key];
                if (setterFunction) {
                    setterFunction(data[key]);
                }
            });

            setPanelLoaded(true);

            const ordersAreLoaded =
                dataSource === DATA_SOURCES.LOJA ||
                (data.orders && data.orders.length > 0);
            setOrdersLoaded(ordersAreLoaded);
        } else {
            setPanelLoaded(false);
            setOrdersLoaded(false);
        }
    }, [dataSource, cachedData, stateSetters]);

    useEffect(() => {
        if (!checkIfCanMakeRequests()) return;
        setPanelLoaded(false);
        setOrdersLoaded(false);
        if (cachedData[dataSource]) {
            const dataFromCache = cachedData[dataSource];
            Object.keys(dataFromCache).forEach(key => {
                const setterFunction = stateSetters[key];
                if (setterFunction) {
                    setterFunction(dataFromCache[key]);
                }
            });
            setPanelLoaded(true);
            setOrdersLoaded(true);
            return;
        }

        const isStoreView = dataSource === DATA_SOURCES.LOJA;
        const quotaEndpoint = isStoreView ? 'store/quota' : 'salesperson/quota';
        const usedApi = isStoreView ? maisVendasApi : api;
        const aggregatedEndpoint = isStoreView
            ? 'store/aggregated-orders'
            : 'salesperson/aggregated-orders';

        const getQuota = async () => {
            const params = {
                startDate: moment(initialDate).format('YYYY-MM-DD'),
                endDate: moment(finalDate).format('YYYY-MM-DD'),
                brand: userProfile.marcaFilial,
            };
            if (isStoreView) {
                params.filialCode =
                    userProfile.users_branches[0].codigo_filial.length === 3
                        ? `000${userProfile.users_branches[0].codigo_filial}`
                        : userProfile.users_branches[0].codigo_filial;
            } else {
                params.salesCode =
                    userProfile.users_sales_code[0].codigo_vendedor;
            }
            const response = await usedApi.get(quotaEndpoint, { params });
            return {
                ...response.data,
                totalQuotaAsNumber: response.data.totalQuota,
                totalQuota: formatPrice(response.data.totalQuota),
            };
        };

        const getAggregatedCapturedOrders = async () => {
            let params;
            if (isStoreView) {
                params = {
                    startDate: moment(initialDate).format('YYYY-MM-DD'),
                    endDate: moment(finalDate).format('YYYY-MM-DD'),
                    filialCode:
                        userProfile.users_branches[0].codigo_filial.length === 3
                            ? `000${userProfile.users_branches[0].codigo_filial}`
                            : userProfile.users_branches[0].codigo_filial,
                    filialBrandId: userProfile.marcaFilial,
                    removeExternalProducts: shouldRemoveExternalProducts(
                        userProfile.marcaFilial
                    ),
                };

                if ([2, 6].includes(userProfile.marcaFilial)) {
                    delete params.removeExternalProducts;
                    params.farmLatam = 'exclude';
                    params.offExternal = 'exclude';
                }

                if (
                    moment(initialDate).format('YYYY-MM-DD') <=
                    moment(beforeExternalOffPremiumDateLimit).format(
                        'YYYY-MM-DD'
                    )
                ) {
                    delete params.removeExternalProducts;
                    params.offExternal = 'exclude';
                }
            } else {
                params = {
                    fatured: 'false',
                    startDate: moment(initialDate).format('YYYY-MM-DD'),
                    endDate: moment(finalDate).format('YYYY-MM-DD'),
                    salesCode: userProfile.users_sales_code.map(
                        salesCodeObj => salesCodeObj.codigo_vendedor
                    ),
                    brands: userProfile.marcaFilial,
                    transaction: 'true',
                    removeExternalProducts: shouldRemoveExternalProducts(
                        userProfile.marcaFilial
                    ),
                };
                if (
                    moment(initialDate).format('YYYY-MM-DD') <=
                    moment(beforeExternalOffPremiumDateLimit).format(
                        'YYYY-MM-DD'
                    )
                ) {
                    delete params.removeExternalProducts;
                    params.offExternal = 'exclude';
                }
                if ([2].includes(userProfile.marcaFilial)) {
                    params.farmLatam = 'exclude';
                }
            }
            return api.get(aggregatedEndpoint, { params });
        };

        const getOrders_StoreView = async () => {
            const params = {
                startDate: moment(initialDate).format('YYYY-MM-DD'),
                endDate: moment(finalDate).format('YYYY-MM-DD'),
                codigo_filial:
                    userProfile.users_branches[0].codigo_filial.length === 3
                        ? `000${userProfile.users_branches[0].codigo_filial}`
                        : userProfile.users_branches[0].codigo_filial,
                brands: userProfile.marcaFilial,
                removeExternalProducts: shouldRemoveExternalProducts(
                    userProfile.marcaFilial
                ),
            };

            if ([2, 6].includes(userProfile.marcaFilial)) {
                delete params.removeExternalProducts;
                params.farmLatam = 'exclude';
                params.offExternal = 'exclude';
            }
            const response = await api.get('store/orders-list', { params });
            return handleOrdersResponse(response.data.data, false);
        };

        const promises = [getAggregatedCapturedOrders(), getQuota()];
        if (isStoreView) promises.push(getOrders_StoreView());

        Promise.all(promises)
            .then(async results => {
                const [aggregatedResponse, quotaResponse] = results;
                const ordersData = isStoreView ? results[2] : [];
                const aggregatedData = aggregatedResponse.data.data;

                const brandIdToFilter = userProfile.marcaFilial.toString();
                const brandSpecificData =
                    aggregatedData.aggregatedByBrand[brandIdToFilter];
                const totalData = brandSpecificData || aggregatedData.total;
                const totalValue =
                    totalData.value + aggregatedData.returns.value;
                const formattedTotal = formatPrice(totalValue);

                const newChartData = isStoreView
                    ? getDateRange(
                          initialDate,
                          finalDate,
                          'YYYY-MM-DD'
                      ).map(date => ({
                          value:
                              aggregatedData.aggregatedByDay[date.label]?.value,
                          label: date.formattedLabel,
                      }))
                    : period === 'day'
                    ? Object.values(
                          aggregatedData.aggregatedByHour
                      ).map(hourObj => ({
                          value: hourObj.value,
                          label: hourObj.prettyHour,
                      }))
                    : getDateRange(
                          initialDate,
                          finalDate,
                          'YYYY-MM-DD'
                      ).map(date => ({
                          value:
                              aggregatedData.aggregatedByDay[date.label]?.value,
                          label: date.formattedLabel,
                      }));

                const processedData = {
                    quota: quotaResponse,
                    aggregatedCapturedOrders: formattedTotal,
                    capturedOrdersIndicators:
                        aggregatedData.aggregatedByChannel,
                    totalOrdersIndicators: aggregatedData.aggregatedByBrand,
                    chartData: newChartData,
                    balanceText: formattedTotal,
                    aggregatedOrdersByPeriod:
                        aggregatedResponse.data.periodSales,
                    orders: ordersData,
                    salesValueAsNumber: Number(
                        formatCurrecyStringToNumberString(formattedTotal)
                    ),
                };

                setCachedData(prev => ({
                    ...prev,
                    [dataSource]: processedData,
                }));

                Object.keys(processedData).forEach(key => {
                    const setterFunction = stateSetters[key];
                    if (setterFunction) {
                        setterFunction(processedData[key]);
                    }
                });

                if (aggregatedResponse.data.msg)
                    Snackbar.info(aggregatedResponse.data.msg);
            })
            .catch(error => {
                if (error.response?.status === 401) {
                    Snackbar.error('Token expirado. Faça login novamente');
                    setShouldLogout(true);
                }
            })
            .finally(() => {
                setPanelLoaded(true);
                if (isStoreView) setOrdersLoaded(true);
            });
    }, [
        dataSource,
        finalDate,
        initialDate,
        userProfile,
        checkIfCanMakeRequests,
        getBrandsParamsForRequests,
        period,
        cachedData,
        stateSetters,
        beforeExternalOffPremiumDateLimit,
    ]);

    useEffect(() => {
        if (dataSource !== DATA_SOURCES.VENDEDOR) {
            setOrders([]);
            return;
        }
        if (!panelLoaded) return;
        if (!checkIfCanMakeRequests()) return;
        setOrdersLoaded(false);

        if (cachedData[DATA_SOURCES.VENDEDOR]?.orders?.length > 0) {
            setOrdersLoaded(true);
            return;
        }

        const getOrders_SalespersonView = async () => {
            const query = new URLSearchParams(location.search);
            const salesCodeFromUrl = query.get('salesCode');
            const params = {
                startDate: moment(initialDate).format('YYYY-MM-DD'),
                endDate: moment(finalDate).format('YYYY-MM-DD'),
                salesCode: userProfile.users_sales_code.map(
                    salesCodeObj => salesCodeObj.codigo_vendedor
                ),
                orderType: isFaturado ? 'invoiced' : 'captured',
                brands: userProfile.marcaFilial,
                transaction: 'true',
                removeExternalProducts: shouldRemoveExternalProducts(
                    userProfile.marcaFilial
                ),
            };

            if (
                moment(initialDate).format('YYYY-MM-DD') <=
                moment(beforeExternalOffPremiumDateLimit).format('YYYY-MM-DD')
            ) {
                delete params.removeExternalProducts;
                params.offExternal = 'exclude';
            }

            if ([2].includes(userProfile.marcaFilial)) {
                params.farmLatam = 'exclude';
            }

            if (salesCodeFromUrl) {
                params.filialCode = userProfile.users_branches[0].codigo_filial;
            }

            const response = await api.get('salesperson/orders', { params });
            const allOrders = await handleOrdersResponse(
                response.data.data,
                false
            );

            const filteredOrders = allOrders.filter(
                order => order.brandId === userProfile.marcaFilial
            );

            return filteredOrders;
        };

        getOrders_SalespersonView()
            .then(formattedOrderData => {
                setOrders(formattedOrderData);
                setCachedData(prevCache => ({
                    ...prevCache,
                    [DATA_SOURCES.VENDEDOR]: {
                        ...prevCache[DATA_SOURCES.VENDEDOR],
                        orders: formattedOrderData,
                    },
                }));
            })
            .catch(() => Snackbar.error('Erro retornando a lista de pedidos'))
            .finally(() => setOrdersLoaded(true));
        // eslint-disable-next-line
    }, [
        dataSource,
        finalDate,
        userProfile,
        isFaturado,
        brandFilter,
        checkIfCanMakeRequests,
        getBrandsParamsForRequests,
        location,
        panelLoaded,
    ]);

    useEffect(() => {
        if (
            !capturedOrdersIndicators ||
            Object.keys(capturedOrdersIndicators).length === 0 ||
            !totalOrdersIndicators ||
            !userProfile
        ) {
            return;
        }

        const newIndicatorsText = {
            totalValue: 0,
            totalAverageValue: 0,
            totalOrderQuantity: 0,
            totalProductQuantity: 0,
            totalAverageQuantity: 0,

            onlineTotalValue: 0,
            onlineAverageValue: 0,
            onlineAverageQuantity: 0,
            onlineOrderQuantity: 0,
            onlineProductQuantity: 0,

            physicalTotalValue: 0,
            physicalAverageValue: 0,
            physicalAverageQuantity: 0,
            physicalOrderQuantity: 0,
            physicalProductQuantity: 0,
        };

        Object.entries(capturedOrdersIndicators).forEach(
            ([indicatorChannel, indicatorValue]) => {
                if (onlineChannels.includes(indicatorChannel)) {
                    newIndicatorsText.onlineTotalValue += indicatorValue.value;
                    newIndicatorsText.onlineOrderQuantity +=
                        indicatorValue.serviceQuantity;
                    newIndicatorsText.onlineProductQuantity +=
                        indicatorValue.productQuantity;
                }
                if (physicalChannels.includes(indicatorChannel)) {
                    newIndicatorsText.physicalTotalValue +=
                        indicatorValue.value;
                    newIndicatorsText.physicalOrderQuantity +=
                        indicatorValue.serviceQuantity;
                    newIndicatorsText.physicalProductQuantity +=
                        indicatorValue.productQuantity;
                }
            }
        );

        newIndicatorsText.onlineAverageQuantity = (
            newIndicatorsText.onlineProductQuantity /
                newIndicatorsText.onlineOrderQuantity || 0
        ).toFixed(2);
        newIndicatorsText.onlineAverageValue =
            newIndicatorsText.onlineTotalValue /
                newIndicatorsText.onlineOrderQuantity || 0;
        newIndicatorsText.physicalAverageQuantity = (
            newIndicatorsText.physicalProductQuantity /
                newIndicatorsText.physicalOrderQuantity || 0
        ).toFixed(2);
        newIndicatorsText.physicalAverageValue =
            newIndicatorsText.physicalTotalValue /
                newIndicatorsText.physicalOrderQuantity || 0;

        if (Object.keys(totalOrdersIndicators).length !== 0) {
            const totalIndicators =
                totalOrdersIndicators[userProfile.marcaFilial];

            if (totalIndicators) {
                newIndicatorsText.totalValue = totalIndicators.value;
                newIndicatorsText.totalOrderQuantity =
                    totalIndicators.serviceQuantity;
                newIndicatorsText.totalProductQuantity =
                    totalIndicators.productQuantity;

                if (
                    dataSource === DATA_SOURCES.VENDEDOR &&
                    userProfile.marcaFilial === ANIMALE
                ) {
                    const totalIndicatorsAllBrands =
                        totalOrdersIndicators[OFFPREMIUM];

                    if (totalIndicatorsAllBrands) {
                        newIndicatorsText.totalValue +=
                            totalIndicatorsAllBrands.value;
                        newIndicatorsText.totalOrderQuantity +=
                            totalIndicatorsAllBrands.serviceQuantity;
                        newIndicatorsText.totalProductQuantity +=
                            totalIndicatorsAllBrands.productQuantity;
                    }
                }

                newIndicatorsText.totalAverageValue =
                    newIndicatorsText.totalValue /
                        newIndicatorsText.totalOrderQuantity || 0;
                newIndicatorsText.totalAverageQuantity = (
                    newIndicatorsText.totalProductQuantity /
                        newIndicatorsText.totalOrderQuantity || 0
                ).toFixed(2);
            }
        }

        const canFormat = [
            'onlineTotalValue',
            'physicalTotalValue',
            'totalValue',
            'totalAverageValue',
            'physicalAverageValue',
            'onlineAverageValue',
        ];

        Object.keys(newIndicatorsText).forEach(key => {
            if (canFormat.includes(key)) {
                newIndicatorsText[key] = formatWithCent(newIndicatorsText[key]);
            }
        });

        setIndicatorsText(newIndicatorsText);
    }, [
        capturedOrdersIndicators,
        totalOrdersIndicators,
        userProfile,
        dataSource,
    ]);

    const isVendedorView = dataSource === DATA_SOURCES.VENDEDOR;
    const allDataLoaded = panelLoaded && ordersLoaded;
    return (
        <>
            <Header />
            <NPSForm
                open={showNPSForm}
                onClose={() => {
                    setShowNPSForm(false);
                    handleSubmit();
                }}
            />
            {!allDataLoaded ? (
                <Box sx={{ padding: '16px' }}>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={200}
                        animation="wave"
                        sx={{ borderRadius: '8px', mb: 2 }}
                    />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={400}
                        animation="wave"
                        sx={{ borderRadius: '8px' }}
                    />
                </Box>
            ) : (
                <>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={2}
                    >
                        <Grid item xs={12} sm="auto">
                            <DateRangePicker hidden={!panelLoaded} assessment />
                        </Grid>
                        <ResponsiveCenteredGridItem
                            item
                            hidden={!panelLoaded}
                            xs={12}
                            sm="auto"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <ToggleContainer onClick={handleToggle}>
                                <ToggleButton
                                    isActive={dataSource === DATA_SOURCES.LOJA}
                                >
                                    <HomeOutlinedIcon />
                                    <span>Loja</span>
                                </ToggleButton>
                                <ToggleButton
                                    isActive={
                                        dataSource === DATA_SOURCES.VENDEDOR
                                    }
                                >
                                    <PersonOutlineOutlinedIcon />
                                    <span>Vendedor</span>
                                </ToggleButton>
                            </ToggleContainer>
                        </ResponsiveCenteredGridItem>
                    </Grid>
                    <DesktopLayout
                        salesNumber={salesValueAsNumber}
                        balanceText={balanceText}
                        quota={quota}
                        salesPercentage={salesPercentage}
                        indicatorsText={indicatorsText}
                        panelLoaded={panelLoaded}
                        ordersLoaded={ordersLoaded}
                        orders={orders}
                        aggregatedOrdersByPeriod={aggregatedOrdersByPeriod}
                        brandFilter={brandFilter}
                        setBrandFilter={setBrandFilter}
                        chartData={chartData}
                        dateRange={dateRange}
                        isVendedorView={isVendedorView}
                    />
                </>
            )}
            {dataSource === DATA_SOURCES.VENDEDOR && <SalesTerms />}
        </>
    );
}
export default CotaCombinada;
